/**
 * AplazoSettlement — página admin que muestra las liquidaciones (settlements)
 * que Aplazo nos manda vía SFTP, junto con stats agregadas y un botón para
 * forzar la sincronización on-demand.
 *
 * Cubre UC-07 + 7.2 de la certification matrix (conciliación de pagos).
 *
 * Backend endpoints:
 *  - GET  /api/admin/aplazo-settlements        → lista + stats
 *  - POST /api/admin/aplazo-settlements/sync   → forzar fetch SFTP
 *
 * No se registra en routes.tsx ni Sidebar.tsx desde aquí — eso lo hace el
 * humano que recibe el componente (instrucción del PRD).
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Banknote,
  RefreshCw,
  Loader2,
  Search,
  AlertCircle,
  CheckCircle2,
  X,
  Calendar,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPost, formatMXN } from '../lib/api'

interface SettlementRow {
  id: string
  fileName: string
  fileDate: string
  loanId: string
  amountSettled: number
  settlementDate: string
  processedAt: string
}

interface SettlementsResponse {
  total: number
  page: number
  limit: number
  pages: number
  data: SettlementRow[]
  stats: {
    monthTotal: number
    monthRows: number
    loansSettled: number
    loansPendingSettlement: number
  }
}

interface SyncResponse {
  ok: true
  empty?: boolean
  file?: string
  message?: string
  inserted?: number
  updated?: number
  errors?: number
  loansUpdated?: number
}

function fmtDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function fmtDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function truncate(s: string, len = 16): string {
  if (!s) return ''
  if (s.length <= len) return s
  return `${s.slice(0, len - 1)}…`
}

export function AplazoSettlement() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [qApplied, setQApplied] = useState('')
  const [loanIdFilter, setLoanIdFilter] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  function notify(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 4000)
  }

  const queryUrl = (() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', '25')
    if (qApplied) params.set('q', qApplied)
    if (loanIdFilter) params.set('loanId', loanIdFilter)
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    return `/api/admin/aplazo-settlements?${params.toString()}`
  })()

  const { data, isLoading, isFetching } = useQuery<SettlementsResponse>({
    queryKey: ['aplazo-settlements', page, qApplied, loanIdFilter, from, to],
    queryFn: () => apiGet(queryUrl),
    placeholderData: (prev) => prev,
  })

  const syncMut = useMutation<SyncResponse>({
    mutationFn: () => apiPost('/api/admin/aplazo-settlements/sync'),
    onSuccess: (res) => {
      if (res.empty) {
        notify('ok', res.message || 'Sin archivos nuevos en el SFTP')
      } else {
        notify(
          'ok',
          `${res.file ?? 'Archivo'}: ${res.inserted ?? 0} nuevas, ${res.updated ?? 0} actualizadas, ${res.errors ?? 0} errores`,
        )
      }
      qc.invalidateQueries({ queryKey: ['aplazo-settlements'] })
    },
    onError: (e: Error) => notify('err', e.message),
  })

  function applySearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setQApplied(q.trim())
  }

  function clearFilters() {
    setQ('')
    setQApplied('')
    setLoanIdFilter('')
    setFrom('')
    setTo('')
    setPage(1)
  }

  const stats = data?.stats

  return (
    <>
      <Topbar
        title="Liquidaciones Aplazo"
        subtitle="Conciliación de pagos transferidos por Aplazo (UC-07)"
        actions={
          <button
            type="button"
            onClick={() => syncMut.mutate()}
            disabled={syncMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {syncMut.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            Sincronizar ahora
          </button>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-ink-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">
              Liquidado este mes
            </div>
            <div className="mt-1 text-2xl font-bold text-ink-900">
              {stats ? formatMXN(stats.monthTotal) : '—'}
            </div>
            <div className="mt-0.5 text-xs text-ink-500">
              {stats?.monthRows ?? 0} rows en {new Date().toLocaleString('es-MX', { month: 'long' })}
            </div>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">
              Loans liquidados
            </div>
            <div className="mt-1 text-2xl font-bold text-ink-900">
              {stats?.loansSettled ?? '—'}
            </div>
            <div className="mt-0.5 text-xs text-emerald-600">Con settlement_date</div>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">
              Pendientes liquidación
            </div>
            <div className="mt-1 text-2xl font-bold text-ink-900">
              {stats?.loansPendingSettlement ?? '—'}
            </div>
            <div className="mt-0.5 text-xs text-amber-600">Activos sin settlement</div>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Filas en BD</div>
            <div className="mt-1 text-2xl font-bold text-ink-900">{data?.total ?? '—'}</div>
            <div className="mt-0.5 text-xs text-ink-500">historial completo</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="mb-4 rounded-2xl border border-ink-200 bg-white p-4">
          <form onSubmit={applySearch} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <label className="mb-1 block text-xs font-semibold text-ink-700">Búsqueda</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-400" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="loan_id o archivo"
                  className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-brand-500"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-700">Loan ID exacto</label>
              <input
                type="text"
                value={loanIdFilter}
                onChange={(e) => {
                  setLoanIdFilter(e.target.value.trim())
                  setPage(1)
                }}
                placeholder="abc-123…"
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-700">Desde</label>
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value)
                  setPage(1)
                }}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-700">Hasta</label>
              <input
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value)
                  setPage(1)
                }}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
            </div>
            <div className="flex gap-2 sm:col-span-2 lg:col-span-5">
              <button
                type="submit"
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
              >
                <Search className="size-3.5" /> Aplicar
              </button>
              {(qApplied || loanIdFilter || from || to) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
                >
                  <X className="size-3.5" /> Limpiar
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Tabla */}
        <div className="rounded-2xl border border-ink-200 bg-white">
          <div className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Banknote className="size-4 text-teal-600" />
              Liquidaciones
              {isFetching && <Loader2 className="size-3.5 animate-spin text-ink-400" />}
            </div>
            <Badge variant="default">{data?.total ?? 0} registros</Badge>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 p-10 text-sm text-ink-500">
              <Loader2 className="size-4 animate-spin" /> Cargando…
            </div>
          ) : (data?.data.length ?? 0) === 0 ? (
            <div className="p-10 text-center text-sm text-ink-500">
              <Calendar className="mx-auto mb-2 size-8 text-ink-300" />
              Aún no hay liquidaciones cargadas. Configura las credenciales SFTP en{' '}
              <strong>/configuracion → Aplazo</strong> y ejecuta "Sincronizar ahora".
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-left text-[10px] uppercase tracking-wider text-ink-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Archivo</th>
                    <th className="px-4 py-2 font-medium">Loan ID</th>
                    <th className="px-4 py-2 font-medium">Monto</th>
                    <th className="px-4 py-2 font-medium">Fecha liquidación</th>
                    <th className="px-4 py-2 font-medium">Procesado</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((s) => (
                    <tr key={s.id} className="border-t border-ink-100">
                      <td className="px-4 py-2 font-mono text-xs text-ink-700" title={s.fileName}>
                        {truncate(s.fileName, 24)}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-ink-900" title={s.loanId}>
                        {truncate(s.loanId, 18)}
                      </td>
                      <td className="px-4 py-2 font-semibold text-ink-900">
                        {formatMXN(s.amountSettled)}
                      </td>
                      <td className="px-4 py-2 text-ink-700">{fmtDate(s.settlementDate)}</td>
                      <td className="px-4 py-2 text-xs text-ink-500">
                        {fmtDateTime(s.processedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data && data.pages > 1 && (
            <div className="flex items-center justify-between border-t border-ink-100 px-5 py-3 text-xs text-ink-600">
              <div>
                Página {data.page} de {data.pages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-ink-200 bg-white px-3 py-1 hover:bg-ink-50 disabled:opacity-40"
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  disabled={page >= data.pages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-ink-200 bg-white px-3 py-1 hover:bg-ink-50 disabled:opacity-40"
                >
                  Siguiente →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
